import { ImageAnalysisResult, UADAnalysisResult } from '@aloft/toolkit-shared';
import { Block, Button, Flex, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { LoadingScreenSamFull } from '../../../components/LoadingScreen/LoadingScreenSamFull.tsx';
import { useRunImageAnalysisAPI } from '../../../hooks/api/toolkitComponents.ts';
import { CraftsmanResults } from './components/CraftsmanResults.tsx';
import { ImageAnalysisForm, ImageAnalysisFormSubmit } from './components/ImageAnalysisForm.tsx';
import { ImageCarousel } from './components/ImageCarousel.tsx';
import { RawTextAccordion } from './components/RawTextAccordion.tsx';
import { TimingBar } from './components/TimingBar.tsx';
import { UADResults } from './components/UADResults.tsx';

export const ImageAnalysisContent = () => {
  const [submittedLabel, setSubmittedLabel] = useState('');
  const [analysisMode, setAnalysisMode] = useState<'craftsmanQuality' | 'uadRatings'>('uadRatings');
  const { mutate, isPending, data, error, reset } = useRunImageAnalysisAPI();

  const handleSubmit = (values: ImageAnalysisFormSubmit) => {
    setSubmittedLabel(values.submittedLabel);
    setAnalysisMode(values.analysisMode);
    const common = {
      mode: values.analysisMode,
      promptOverride: values.promptOverride,
      compress: values.compress,
      sample: values.sample,
    };
    if (values.lookupType === 'mlsId') {
      mutate({
        body: { ...common, lookupType: 'mlsId', mlsid: values.mlsid, listingID: values.listingID },
      });
    } else {
      mutate({ body: { ...common, lookupType: 'address', address: values.address } });
    }
  };

  if (isPending) {
    return (
      <Block position="relative" minHeight="400px">
        <LoadingScreenSamFull
          isLoading
          what="Image Analysis"
          wrapperProps={{ position: 'absolute', zIndex: '10' }}
        />
      </Block>
    );
  }

  if (data != null || error != null) {
    const timing = data?._debug?.timing;
    const tokenUsage = data?._debug?.tokenUsage;
    const imageURLs = data?._debug?.imageURLs ?? [];
    return (
      <Flex flexDirection="column" gap="20px">
        <Flex justifyContent="space-between" alignItems="center">
          {submittedLabel !== '' && <Typography variant="h2">{submittedLabel}</Typography>}
          <Button text="New Analysis" onClick={() => reset()} size="sm" />
        </Flex>

        {error != null && (
          <Block padding="12px" borderRadius="4px" backgroundColor="alertErrorLight">
            <Typography variant="body1">
              {(error.payload as any)?.message ?? 'An unknown error occurred'}
            </Typography>
          </Block>
        )}

        {timing != null && <TimingBar timing={timing} tokenUsage={tokenUsage} />}

        {data?._debug?.promptBlockReason != null && (
          <Block padding="10px 16px" borderRadius="4px" backgroundColor="alertWarningLight">
            <Typography variant="body2">
              Prompt blocked by Gemini — reason: <strong>{data._debug.promptBlockReason}</strong>
            </Typography>
          </Block>
        )}

        {data != null && analysisMode === 'craftsmanQuality' && (
          <CraftsmanResults data={data as ImageAnalysisResult} />
        )}
        {data != null && analysisMode === 'uadRatings' && (
          <UADResults data={data as UADAnalysisResult} />
        )}

        {imageURLs.length > 0 && <ImageCarousel urls={imageURLs} />}

        {data?._debug?.rawText != null && <RawTextAccordion rawText={data._debug.rawText} />}
      </Flex>
    );
  }

  return <ImageAnalysisForm onSubmit={handleSubmit} isSubmitting={isPending} />;
};
